import { formatUnits } from "viem";
import { notEmpty, toDate } from "@orb-labs/types";
import { ONE_BILLION, ONE_MILLION, ONE_USD_RAW } from "../bullrun/constants.js";
const TRILLION = 1_000_000_000_000;
const BILLION = Number(ONE_BILLION);
const MILLION = Number(ONE_MILLION);
const THOUSAND = 1000;
/**
 * Converts a raw BigInt-encoded USD value (fiat decimals) to a human number.
 */
export function rawToUsd(raw) {
    if (!notEmpty(raw) || raw === "")
        return 0;
    const bi = BigInt(raw);
    const quotient = bi / ONE_USD_RAW;
    const remainder = bi % ONE_USD_RAW;
    return Number(quotient) + Number(remainder) / Number(ONE_USD_RAW);
}
/**
 * Estimates USD market cap at the time of a spot trade: mcap_now × (price_trade / price_now),
 * using the trade's implied price (fiat notional ÷ token amount). This matches true historical
 * mcap when circulating supply is unchanged; supply shifts are not modeled.
 */
export function formatEstimatedMarketCapAtTrade(trade, currentMarketCapUsdRaw, currentPriceUsdRaw) {
    if (!notEmpty(currentMarketCapUsdRaw) || !notEmpty(currentPriceUsdRaw))
        return "--";
    const fiat = trade.amount?.fiatValue();
    if (!fiat)
        return "--";
    const fiatUsd = rawToUsd(fiat.toRawAmount());
    const tokenAmt = parseFloat(trade.amount.toDecimalString(18));
    if (!(tokenAmt > 0) || !Number.isFinite(tokenAmt))
        return "--";
    const priceNowUsd = rawToUsd(currentPriceUsdRaw);
    const mcapNowUsd = rawToUsd(currentMarketCapUsdRaw);
    if (!(priceNowUsd > 0) || !(mcapNowUsd > 0))
        return "--";
    const priceTradeUsd = fiatUsd / tokenAmt;
    if (!(priceTradeUsd > 0) || !Number.isFinite(priceTradeUsd))
        return "--";
    const mcapAtTradeUsd = mcapNowUsd * (priceTradeUsd / priceNowUsd);
    if (!Number.isFinite(mcapAtTradeUsd))
        return "--";
    return formatCompactUSD(mcapAtTradeUsd);
}
/**
 * Formats a raw BigInt USD value as a compact dollar string.
 * e.g. "$1.22B", "$204.1M", "$15.83M"
 */
export function formatUsd(raw) {
    const value = rawToUsd(raw);
    const abs = Math.abs(value);
    if (abs >= TRILLION)
        return `$${(value / TRILLION).toFixed(2)}T`;
    if (abs >= BILLION)
        return `$${(value / BILLION).toFixed(2)}B`;
    if (abs >= MILLION)
        return `$${(value / MILLION).toFixed(2)}M`;
    if (abs >= THOUSAND)
        return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (abs >= 1)
        return `$${value.toFixed(2)}`;
    if (abs === 0)
        return "$0.00";
    const sigDigits = -Math.floor(Math.log10(abs)) + 1;
    return `$${abs.toFixed(sigDigits)}`;
}
/**
 * Formats a percentage change value.
 * e.g. "+16.02%", "-54.51%"
 */
export function formatPercentChange(value) {
    if (!notEmpty(value) || value === "")
        return "--";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (Number.isNaN(num))
        return "--";
    const abs = Math.abs(num);
    const sign = num >= 0 ? "+" : "";
    if (abs >= BILLION)
        return `${sign}${(num / BILLION).toFixed(1)}B%`;
    if (abs >= MILLION)
        return `${sign}${(num / MILLION).toFixed(1)}M%`;
    if (abs >= THOUSAND)
        return `${sign}${num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    return `${sign}${num.toFixed(2)}%`;
}
export function formatCompactNumber(value) {
    if (!Number.isFinite(value))
        return "--";
    const abs = Math.abs(value);
    if (abs === 0)
        return "0";
    if (abs >= TRILLION)
        return `${(value / TRILLION).toLocaleString("en-US", { maximumFractionDigits: 2 })}T`;
    if (abs >= BILLION)
        return `${(value / BILLION).toLocaleString("en-US", { maximumFractionDigits: 2 })}B`;
    if (abs >= MILLION)
        return `${(value / MILLION).toLocaleString("en-US", { maximumFractionDigits: 2 })}M`;
    if (abs >= THOUSAND)
        return `${(value / THOUSAND).toLocaleString("en-US", { maximumFractionDigits: 2 })}K`;
    if (abs >= 1)
        return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
    if (abs >= 0.0001)
        return value.toFixed(4);
    return "<0.0001";
}
/**
 * Adds thousands separators to a decimal string without converting through Number,
 * avoiding IEEE-754 precision loss for large token amounts.
 */
export function formatDecimalStringForDisplay(decimalStr) {
    const trimmed = decimalStr.trim();
    if (!trimmed)
        return "0";
    const negative = trimmed.startsWith("-");
    const abs = negative ? trimmed.slice(1) : trimmed;
    const [intPart, fracPart] = abs.split(".");
    const intWithCommas = (intPart || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    const out = notEmpty(fracPart) ? `${intWithCommas}.${fracPart}` : intWithCommas;
    return negative ? `-${out}` : out;
}
export function formatRelativeTime(date) {
    const d = toDate(date);
    if (!d)
        return "";
    const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
    if (diff < 60)
        return `${diff}s`;
    if (diff < 3600)
        return `${Math.floor(diff / 60)}m`;
    if (diff < 86400)
        return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
}
/**
 * Remaining time until a future instant (e.g. resolution).
 * For elapsed time since a past date, use {@link formatRelativeTime}.
 */
export function formatTimeUntil(date) {
    const d = toDate(date);
    if (!d)
        return "";
    const diff = Math.max(0, Math.floor((d.getTime() - Date.now()) / 1000));
    if (diff < 3600)
        return `${Math.ceil(diff / 60)}m left`;
    if (diff < 86400)
        return `${Math.floor(diff / 3600)}h left`;
    const days = Math.floor(diff / 86400);
    if (days < 30)
        return `${days}d left`;
    const months = Math.floor(days / 30);
    const remainingDays = days % 30;
    if (remainingDays === 0)
        return `${months}mo left`;
    return `${months}mo ${remainingDays}d left`;
}
export const initialsFromSymbol = (symbol) => {
    const clean = symbol.replace(/[^A-Za-z0-9]/g, "");
    if (clean.length === 0) {
        return "";
    }
    return clean.length <= 2 ? clean.toUpperCase() : clean.slice(0, 2).toUpperCase();
};
const formatWithSuffix = (value, decimals = 1) => {
    if (value >= TRILLION) {
        return `${(value / TRILLION).toLocaleString("en-US", { maximumFractionDigits: 2 })}T`;
    }
    else if (value >= BILLION) {
        return `${(value / BILLION).toLocaleString("en-US", { maximumFractionDigits: 2 })}B`;
    }
    else if (value >= MILLION) {
        return `${(value / MILLION).toLocaleString("en-US", { maximumFractionDigits: 2 })}M`;
    }
    else if (value >= THOUSAND) {
        return `${value.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    }
    return value.toFixed(decimals);
};
export const formatPercent = (value, decimals = 2) => {
    const absValue = Math.abs(value);
    if (absValue >= TRILLION) {
        return `${(value / TRILLION).toFixed(1)}T%`;
    }
    else if (absValue >= BILLION) {
        return `${(value / BILLION).toFixed(1)}B%`;
    }
    else if (absValue >= MILLION) {
        return `${(value / MILLION).toFixed(1)}M%`;
    }
    else if (absValue >= THOUSAND) {
        return `${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
    }
    else if (absValue > 0 && absValue < 0.01) {
        return `${value < 0 ? "-" : ""}<0.01%`;
    }
    return `${value.toFixed(decimals)}%`;
};
/** Values safe for `rawToUsd` / `BigInt` without throwing (micro-USD wire). */
const INTEGER_STRING_PATTERN = /^-?\d+$/;
function isMicroUsdRawVolumeValue(value) {
    if (typeof value === "bigint")
        return true;
    if (typeof value === "number")
        return Number.isFinite(value) && Number.isInteger(value);
    if (typeof value === "string")
        return INTEGER_STRING_PATTERN.test(value);
    return false;
}
export const formatCompactUSD = (value, decimals) => {
    const abs = Math.abs(value);
    if (abs >= TRILLION)
        return `$${(value / TRILLION).toFixed(1)}T`;
    if (abs >= BILLION)
        return `$${(value / BILLION).toFixed(1)}B`;
    if (abs >= MILLION)
        return `$${(value / MILLION).toFixed(1)}M`;
    if (abs >= 10_000)
        return `$${Math.round(value / THOUSAND)}K`;
    if (abs >= THOUSAND)
        return `$${(value / THOUSAND).toFixed(1)}K`;
    if (abs >= 100)
        return `$${Math.round(value)}`;
    if (notEmpty(decimals))
        return `$${value.toFixed(decimals)}`;
    if (abs >= 1)
        return `$${value.toFixed(2)}`;
    if (abs === 0)
        return "$0.00";
    const sign = value < 0 ? "-" : "";
    const sigDigits = -Math.floor(Math.log10(abs)) + 1;
    return `${sign}$${abs.toFixed(sigDigits)}`;
};
/** Compact USD string for raw micro-USD volume fields; omits zero/invalid. */
export function formatVolumeUsdRaw(raw) {
    if (!notEmpty(raw) || !isMicroUsdRawVolumeValue(raw))
        return undefined;
    let usd;
    try {
        usd = rawToUsd(raw);
    }
    catch {
        return undefined;
    }
    if (!Number.isFinite(usd) || usd <= 0)
        return undefined;
    return formatCompactUSD(usd);
}
/** Same as {@link formatVolumeUsdRaw} for GraphQL-style `{ end: { value } }` volume fields. */
export function formatVolumeUsdField(vol) {
    const raw = vol?.end?.value;
    if (!isMicroUsdRawVolumeValue(raw))
        return undefined;
    return formatVolumeUsdRaw(raw);
}
export const formatTokenAmount = (rawAmount, decimals, maxDecimals = 8) => {
    if (!notEmpty(rawAmount) || rawAmount === BigInt(0))
        return "0";
    const str = formatUnits(rawAmount, decimals);
    const num = parseFloat(str);
    if (num >= 1000)
        return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
    if (num >= 1)
        return num.toFixed(Math.min(2, maxDecimals));
    if (num === 0)
        return "0";
    return num.toFixed(Math.min(maxDecimals, 8));
};
export const formatValueInUSD = (value) => {
    if (!notEmpty(value))
        return "--";
    const raw = typeof value === "bigint" ? value : BigInt(value ?? 0);
    const numValue = rawToUsd(raw);
    // Sub-thousand values (typical unit prices) need significant-digit formatting
    // so sub-cent tokens don't render as "$0.00".
    if (numValue < THOUSAND) {
        return formatUsd(raw);
    }
    return `$${formatWithSuffix(numValue, 2)}`;
};
export const formatLargeNumber = (value) => {
    if (!notEmpty(value))
        return "--";
    const numValue = Number(value);
    if (numValue < THOUSAND) {
        return value.toString();
    }
    return formatWithSuffix(numValue);
};
export const formatAmount = (amount) => {
    if (!notEmpty(amount))
        return "--";
    const numValue = Number(amount.toFixed(2));
    const absValue = Math.abs(numValue);
    const negative = numValue < 0;
    if (absValue >= THOUSAND) {
        const formatted = formatWithSuffix(Number(absValue.toFixed()));
        return negative ? `-${formatted}` : formatted;
    }
    if (absValue >= 100) {
        return amount.toFixed(3);
    }
    if (absValue >= 10) {
        return amount.toFixed(4);
    }
    if (absValue >= 1) {
        return amount.toFixed(5);
    }
    if (amount.equalTo(0)) {
        return "0.00";
    }
    return amount.toSignificant(5);
};
export const formatDate = (value) => {
    const date = toDate(value);
    if (!date)
        return "--";
    const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    const getDaySuffix = (d) => {
        if (d === 1 || d === 21 || d === 31)
            return "st";
        if (d === 2 || d === 22)
            return "nd";
        if (d === 3 || d === 23)
            return "rd";
        return "th";
    };
    return `${month} ${day}${getDaySuffix(day)} ${year}`;
};
export const formatShortDate = (value) => {
    const date = toDate(value);
    if (!date)
        return "--";
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
};
export const formatShortDateWithTime = (value) => {
    const date = toDate(value);
    if (!date || Number.isNaN(date.getTime()))
        return "--";
    const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
    });
    return dateTimeFormat.format(date);
};
export const formatSeriesDate = (value) => {
    const d = toDate(value);
    if (!d || Number.isNaN(d.getTime()))
        return "--";
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getMonth()]} ${d.getDate()}`;
};
export const formatCents = (frac) => {
    const cents = frac * 100;
    if (cents >= 100)
        return "100¢";
    if (cents <= 0)
        return "0¢";
    return `${cents % 1 === 0 ? cents.toFixed(0) : cents.toFixed(1)}¢`;
};
export const formatChangeLabel = (value, zeroLabel = "- No change") => {
    if (value === 0)
        return zeroLabel;
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}`;
};
export const formatPnlWithColor = (value) => {
    if (!notEmpty(value)) {
        return { text: "--", color: "#ffffff" };
    }
    if (value >= 0) {
        return {
            text: `+$${Math.abs(value).toFixed(2)}`,
            color: "#00C805",
        };
    }
    return {
        text: `-$${Math.abs(value).toFixed(2)}`,
        color: "#EB5D2A",
    };
};
export const truncateAddress = (address) => {
    if (!address)
        return "--";
    if (address.length <= 10)
        return address;
    return `${address.slice(0, 4)}...${address.slice(-4)}`;
};
